import React, { useState } from 'react';
import { PanelLeftIcon, MessageSquareIcon, NotebookPenIcon, RepeatIcon, PlusIcon } from 'lucide-react';
import { SidebarSection } from './SidebarSection';
import { UserProfile } from './UserProfile';
import { mockData } from '../utils/mockData';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from './common/Logo';
interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}
type Domain = 'chat' | 'notes' | 'passons';
export const Sidebar = ({
  isCollapsed,
  onToggleCollapse
}: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [dragOverIncident, setDragOverIncident] = useState<string | null>(null);
  const getActiveDomain = (): Domain => {
    if (location.pathname === '/' || location.pathname.startsWith('/chat')) return 'chat';
    if (location.pathname.startsWith('/notes')) return 'notes';
    if (location.pathname.startsWith('/passons')) return 'passons';
    return 'chat';
  };
  const activeDomain = getActiveDomain();
  const handleDragOver = (e: React.DragEvent, incidentId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIncident(incidentId);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIncident(null);
  };
  const handleDrop = (e: React.DragEvent, incidentTitle: string) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const noteIds = JSON.parse(e.dataTransfer.getData('application/json'));
      alert(`Tagged ${noteIds.length} note(s) to incident: ${incidentTitle}`);
      setDragOverIncident(null);
    } catch (err) {
      console.error('Drop failed:', err);
    }
  };
  const SidebarLink = ({
    to,
    children,
    incidentId,
    incidentTitle
  }: {
    to: string;
    children: React.ReactNode;
    incidentId?: string;
    incidentTitle?: string;
  }) => <Link to={to} onDragOver={incidentId ? e => handleDragOver(e, incidentId) : undefined} onDragLeave={incidentId ? handleDragLeave : undefined} onDrop={incidentId && incidentTitle ? e => handleDrop(e, incidentTitle) : undefined} className={`block p-2 mb-1 rounded text-sm truncate transition-all duration-200 ${location.pathname === to ? 'bg-[#1A2230] text-[#73A7FF] border-l-[3px] border-[#6FA1FF] pl-[13px] shadow-[inset_4px_0_0_-1px_#6FA1FF]' : 'text-command-text-muted hover:bg-[#1a2230]/50 pl-4'} ${dragOverIncident === incidentId ? 'ring-2 ring-[#4a90e2] bg-[#2d4c6b]' : ''}`} data-id="element-161">
      {children}
    </Link>;
  const domains = [{
    id: 'chat',
    icon: MessageSquareIcon,
    label: 'Blue Partner'
  }, {
    id: 'notes',
    icon: NotebookPenIcon,
    label: 'Field Notes'
  }, {
    id: 'passons',
    icon: RepeatIcon,
    label: 'Pass Ons'
  }];
  return <aside className="flex h-screen bg-command-sidebar border-r border-command-border-strong transition-all duration-300" data-id="element-162">
      {/* Domain Rail */}
      <div className="w-[64px] bg-command-rail flex flex-col items-center py-4 gap-4 border-r border-command-border-soft flex-shrink-0" data-id="element-163">
        <button onClick={() => navigate('/')} className="mb-4 hover:opacity-80 transition-opacity" data-id="element-164">
          <Logo size={32} className="text-[#4a90e2]" data-id="element-165" />
        </button>

        {domains.map(domain => <button key={domain.id} onClick={() => {
        if (domain.id === 'chat') navigate('/');
        if (domain.id === 'notes') navigate('/notes/feed');
        if (domain.id === 'passons') navigate('/passons');
      }} className={`p-3 rounded-xl transition-all duration-200 group relative ${activeDomain === domain.id ? 'bg-[#1A2230] text-[#73A7FF]' : 'text-[#666666] hover:bg-[#2a2a2a] hover:text-[#e0e0e0]'}`} title={domain.label} data-id="element-166">
            <domain.icon size={24} data-id="element-167" />
            {activeDomain === domain.id && <div className="absolute left-0 top-1/4 bottom-1/4 w-[3px] bg-[#6FA1FF] rounded-r-full" data-id="element-168" />}
          </button>)}
        <div className="mt-auto" data-id="element-169">
          <button onClick={onToggleCollapse} className="p-3 text-[#666666] hover:text-[#e0e0e0] transition-colors" data-id="element-170">
            <PanelLeftIcon size={20} data-id="element-171" />
          </button>
        </div>
      </div>
      {/* Sidebar Content */}
      <div className={`flex flex-col transition-all duration-300 overflow-hidden ${isCollapsed ? 'w-0 opacity-0' : 'w-[236px] opacity-100'}`} data-id="element-172">
        <div className="h-12 px-4 flex items-center justify-between border-b border-command-border-soft whitespace-nowrap" data-id="element-173">
          <span className="text-xs font-bold uppercase tracking-wider text-command-text-muted" data-id="element-174">
            {domains.find(d => d.id === activeDomain)?.label}
          </span>
        </div>

        <div className="flex-grow overflow-y-auto px-6 pt-2" data-id="element-175">
          <button onClick={() => navigate('/')} className="flex items-center gap-3 w-full p-2 mb-6 text-[#73A7FF] hover:text-[#8db3ff] text-sm font-bold transition-all group" data-id="element-176">
            <div className="w-8 h-8 flex items-center justify-center" data-id="element-177">
              <PlusIcon size={18} data-id="element-178" />
            </div>
            <span data-id="element-179">New Chat</span>
          </button>

          <nav className="space-y-6 pb-8" data-id="element-180">
            <div className="mt-[-8px]" data-id="element-181">
              <SidebarSection title="Active Incidents" defaultOpen={true} data-id="element-182">
                <ul data-id="element-183">
                  {mockData.sidebar.activeIncidents.slice(0, 3).map(item => <li key={item.id} data-id="element-184">
                      <SidebarLink to={`/notes/incident/${item.id}`} incidentId={item.id} incidentTitle={item.title} data-id="element-185">
                        {item.title}
                      </SidebarLink>
                    </li>)}
                </ul>
              </SidebarSection>
            </div>

            <SidebarSection title="Chat History" defaultOpen={true} data-id="element-186">
              <ul data-id="element-187">
                {mockData.sidebar.chatHistory.map(item => <li key={item.id} data-id="element-188">
                    <SidebarLink to={`/chat/${item.id}`} data-id="element-189">
                      {item.title}
                    </SidebarLink>
                  </li>)}
              </ul>
            </SidebarSection>
          </nav>
        </div>
        <UserProfile data-id="element-190" />
      </div>
    </aside>;
};